import { c as _c } from "react/compiler-runtime";
import { Card } from '@/components/ui/card';
import { LinkComponent } from '@/components/ui/link-component';
import { SectionLabel } from '@/components/ui/section-label';
import { withLocaleParameter } from '@/features/payload-cms/payload-cms/utils/document-download-name';
import type { Document, FileDownloadBlock } from '@/features/payload-cms/payload-types';
import type { Locale } from '@/types/types';
import { Download, FileArchive, FileImage, FileSpreadsheet, FileText } from 'lucide-react';
import type React from 'react';
export type FileDownloadType = FileDownloadBlock & {
  locale: Locale;
};

/** A document the visitor may read. Without read access, Payload leaves the bare id. */
type DownloadableFile = Document & {
  url: string;
  filename: string;
};
const isDownloadable = (file: unknown): file is DownloadableFile => typeof file === 'object' && file !== null && typeof (file as Document).url === 'string' && typeof (file as Document).filename === 'string';
const formatBytes = (bytes: number, decimals = 2): string => {
  // if < 1kb
  if (bytes < 1024) return `${bytes} Bytes`;
  // if < 1mb
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(decimals)} KB`;
  // if < 1gb
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(decimals)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(decimals)} GB`;
};
const dateStringToFormatedDate = (locale: Locale, dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  };
  return date.toLocaleDateString(locale, options);
};

/** The extension of the stored file, like "PDF" or "DOCX", which is what visitors know a type by. */
const fileTypeLabel = (filename: string): string | undefined => {
  const dot = filename.lastIndexOf('.');
  return dot > 0 && dot < filename.length - 1 ? filename.slice(dot + 1).toUpperCase() : undefined;
};
const FileTypeIcon: React.FC<{
  mimeType: string;
}> = t0 => {
  const $ = _c(4);
  const {
    mimeType
  } = t0;
  if (mimeType.startsWith("image/")) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FileImage className="text-conveniat-green h-5 w-5" aria-hidden="true" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FileSpreadsheet className="text-conveniat-green h-5 w-5" aria-hidden="true" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (mimeType.includes("zip") || mimeType.includes("compressed")) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FileArchive className="text-conveniat-green h-5 w-5" aria-hidden="true" />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FileText className="text-conveniat-green h-5 w-5" aria-hidden="true" />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const FilePreview: React.FC<{
  thumbnailUrl: string | undefined;
  mimeType: string;
  typeLabel: string | undefined;
}> = t0 => {
  const $ = _c(9);
  const {
    thumbnailUrl,
    mimeType,
    typeLabel
  } = t0;
  if (thumbnailUrl !== undefined && thumbnailUrl !== "") {
    let t1;
    if ($[0] !== thumbnailUrl) {
      t1 = <span className="border-line flex h-14 w-11 shrink-0 overflow-hidden rounded-md border bg-white"><img src={thumbnailUrl} alt="" loading="lazy" decoding="async" className="size-full object-cover object-top" /></span>;
      $[0] = thumbnailUrl;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== mimeType) {
    t1 = <FileTypeIcon mimeType={mimeType} />;
    $[2] = mimeType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== typeLabel) {
    t2 = typeLabel !== undefined && <span className="border-line absolute -right-1.5 -bottom-1 rounded-[4px] border bg-white px-1 text-[8px] leading-[1.4] font-bold text-green-700">{typeLabel}</span>;
    $[4] = typeLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span className="relative flex h-10 w-11 shrink-0 items-center justify-center rounded-lg bg-green-50 transition duration-300 group-hover:bg-green-100/60">{t1}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

/**
 * One document as visitors know it from every download list: its thumbnail or type icon, its name,
 * type, size and date, and the download arrow. Also used by the site search.
 */
export const DocumentDownloadRow: React.FC<{
  href: string;
  displayName: string;
  filename: string;
  mimeType: string;
  thumbnailUrl: string | undefined;
  filesize: number | undefined;
  updatedAt: string;
  openInNewTab: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(30);
  const {
    href,
    displayName,
    filename,
    mimeType,
    thumbnailUrl,
    filesize,
    updatedAt,
    openInNewTab,
    locale
  } = t0;
  let t1;
  if ($[0] !== filename) {
    t1 = fileTypeLabel(filename);
    $[0] = filename;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const typeLabel = t1;
  let t2;
  if ($[2] !== filesize) {
    t2 = filesize === undefined ? undefined : formatBytes(filesize);
    $[2] = filesize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== locale || $[5] !== updatedAt) {
    t3 = dateStringToFormatedDate(locale, updatedAt);
    $[4] = locale;
    $[5] = updatedAt;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== typeLabel) {
    t4 = [typeLabel, t2, t3].filter(_temp);
    $[7] = t2;
    $[8] = t3;
    $[9] = typeLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const details = t4;
  let t5;
  if ($[11] !== mimeType || $[12] !== thumbnailUrl || $[13] !== typeLabel) {
    t5 = <FilePreview thumbnailUrl={thumbnailUrl} mimeType={mimeType} typeLabel={typeLabel} />;
    $[11] = mimeType;
    $[12] = thumbnailUrl;
    $[13] = typeLabel;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== displayName) {
    t6 = <span className="text-conveniat-green group-hover:text-conveniat-green/80 block truncate text-sm font-semibold transition duration-200" title={displayName}>{displayName}</span>;
    $[15] = displayName;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = details.join(" \u2022 ");
  let t8;
  if ($[17] !== t7) {
    t8 = <span className="mt-0.5 text-xs text-gray-500">{t7}</span>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = <div className="flex min-w-0 flex-col">{t6}{t8}</div>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="group-hover:bg-conveniat-green flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition-all duration-300 group-hover:scale-105 group-hover:text-white"><Download className="h-4 w-4" aria-hidden="true" /></div>;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t5 || $[24] !== t9) {
    t11 = <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:gap-4">{t5}{t9}{t10}</div>;
    $[23] = t5;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== href || $[27] !== openInNewTab || $[28] !== t11) {
    t12 = <li className="group transition-colors duration-300 hover:bg-green-50/40"><LinkComponent href={href} openInNewTab={openInNewTab} className="block p-3 sm:p-4" hideExternalIcon={true}>{t11}</LinkComponent></li>;
    $[26] = href;
    $[27] = openInNewTab;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
const FileRow: React.FC<{
  file: DownloadableFile;
  openInNewTab: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(15);
  const {
    file,
    openInNewTab,
    locale
  } = t0;
  const t1 = file.title ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = t1.trim();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const title = t2;
  let t3;
  if ($[2] !== file.url || $[3] !== locale) {
    t3 = withLocaleParameter(file.url, locale);
    $[2] = file.url;
    $[3] = locale;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = title === "" ? file.filename : title;
  const t5 = file.mimeType ?? "";
  const t6 = file.pdfThumbnailUrl ?? undefined;
  const t7 = typeof file.filesize === "number" ? file.filesize : undefined;
  let t8;
  if ($[5] !== file.filename || $[6] !== file.updatedAt || $[7] !== locale || $[8] !== openInNewTab || $[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <DocumentDownloadRow href={t3} displayName={t4} filename={file.filename} mimeType={t5} thumbnailUrl={t6} filesize={t7} updatedAt={file.updatedAt} openInNewTab={openInNewTab} locale={locale} />;
    $[5] = file.filename;
    $[6] = file.updatedAt;
    $[7] = locale;
    $[8] = openInNewTab;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};

/**
 * The documents of a file download block, one list per group under the group's title. Files the
 * visitor may not read, and groups left without any, are not shown.
 */
export const FileDownload: React.FC<FileDownloadType> = t0 => {
  const $ = _c(14);
  const {
    groups,
    openInNewTab,
    locale
  } = t0;
  let t1;
  if ($[0] !== groups) {
    t1 = Array.isArray(groups) ? groups : [];
    $[0] = groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] !== locale || $[3] !== openInNewTab || $[4] !== t1) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleGroups = t1.map(_temp3).filter(_temp4);
      if (visibleGroups.length === 0) {
        t4 = undefined;
        break bb0;
      }
      t2 = "my-4 flex flex-col gap-5 sm:my-6";
      let t5;
      if ($[8] !== locale || $[9] !== openInNewTab) {
        t5 = (group_1, index) => <div key={group_1.id ?? index}>{group_1.title !== "" && <SectionLabel as="h3" className="mb-2">{group_1.title}</SectionLabel>}<Card as="ul" divided={true}>{group_1.files.map(file_0 => <FileRow key={file_0.id} file={file_0} openInNewTab={openInNewTab === true} locale={locale} />)}</Card></div>;
        $[8] = locale;
        $[9] = openInNewTab;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      t3 = visibleGroups.map(t5);
    }
    $[2] = locale;
    $[3] = openInNewTab;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3) {
    t5 = <div className={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
function _temp(detail) {
  return detail !== undefined;
}
function _temp2(file) {
  return isDownloadable(file);
}
function _temp3(group) {
  return {
    id: group.id,
    title: (group.title ?? "").trim(),
    files: (Array.isArray(group.files) ? group.files : []).filter(_temp2)
  };
}
function _temp4(group_0) {
  return group_0.files.length > 0;
}